import { formatAmountWithUnit } from "@loot-os/api-contract";
import { Button } from "@loot-os/ui/components/button";
import { Card, CardAction, CardContent, CardHeader, CardTitle } from "@loot-os/ui/components/card";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@loot-os/ui/components/table";
import { formatNumber } from "@loot-os/ui/lib/format";
import { RefreshCw } from "lucide-react";
import { useDeferredValue, useMemo, useState } from "react";
import { useCurrency } from "../lib/currency";
import { isWholeTable, SIM_TRIALS, simulateDropTable } from "../lib/dropTableSim";

/**
 * Boxed's "Sim 10 … Sim 10000", as a spread: over each number of opens, what a shopper wins per
 * open on average, at the 5th percentile, the median and the 95th of `SIM_TRIALS` runs. Beside
 * the price that reads as how often a run of that length comes out ahead.
 *
 * It reruns on every edit, against a deferred copy of the table so typing stays responsive, and
 * Resim draws a fresh set of runs over the same table.
 */
export function DropTableSims({
  items,
  price,
}: {
  items: readonly { weight: number; value: number }[];
  /** The box price in cents, when there is one, to read each figure against. */
  price: number | null;
}) {
  const currency = useCurrency();
  const [seed, setSeed] = useState(1);
  const deferred = useDeferredValue(items);
  const results = useMemo(() => simulateDropTable(deferred, seed), [deferred, seed]);
  const priced = price !== null && price > 0;
  const amount = (cents: number) => formatAmountWithUnit(Math.round(cents), currency);
  const ofPrice = (cents: number) =>
    priced ? (
      <span className="ml-1 text-xs text-muted-foreground">
        {((cents / price) * 100).toFixed(0)}%
      </span>
    ) : null;

  return (
    <Card>
      <CardHeader>
        <CardTitle>Simulations</CardTitle>
        <CardAction>
          <Button
            type="button"
            variant="outline"
            size="sm"
            onClick={() => setSeed((s) => s + 1)}
            disabled={results.length === 0}
          >
            <RefreshCw />
            Resim
          </Button>
        </CardAction>
      </CardHeader>
      <CardContent className="flex flex-col gap-3">
        {results.length === 0 ? (
          <p className="text-sm text-muted-foreground">
            Give the table some weight to simulate it.
          </p>
        ) : (
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Opens</TableHead>
                <TableHead className="text-right">Unlucky run (5th pct)</TableHead>
                <TableHead className="text-right">Typical run</TableHead>
                <TableHead className="text-right">Lucky run (95th pct)</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {results.map((result) => (
                <TableRow key={result.opens}>
                  <TableCell>{formatNumber(result.opens)}</TableCell>
                  <TableCell className="text-right font-mono">
                    {amount(result.p5)}
                    {ofPrice(result.p5)}
                  </TableCell>
                  <TableCell className="text-right font-mono">
                    {amount(result.median)}
                    {ofPrice(result.median)}
                  </TableCell>
                  <TableCell className="text-right font-mono">
                    {amount(result.p95)}
                    {ofPrice(result.p95)}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        )}
        <p className="text-xs text-muted-foreground">
          Average won per open across {formatNumber(SIM_TRIALS)} simulated runs of each length
          {priced ? ", with each figure as a share of the price" : ""}.
          {results.length > 0 && !isWholeTable(deferred)
            ? " The weights do not total 100% yet, so rows roll against the weight they have."
            : ""}
        </p>
      </CardContent>
    </Card>
  );
}
